<script>
import FormGroup from '@maison/components/form/FormGroup.vue';
import TextField from '@maison/components/form/fields/TextField.vue';
import PhoneField from '@maison/components/form/fields/PhoneField.vue';
import SelectField from './fields/SelectField.vue';
import TextareaField from './fields/TextareaField.vue';
import CheckboxField from '@maison/components/form/fields/CheckboxField.vue';
import formBasicMixin from '@phoenix/mixins/formBasicMixin';
import formAddressFieldsMixin from '@phoenix/mixins/formAddressFieldsMixin';
import formContactMixin from '@phoenix/mixins/formContactMixin';
import formWithImage from '@maison/mixins/formWithImage';
import PictureLazy from '@maison/components/common/PictureLazy';
import { defineAsyncComponent } from 'vue';

export default {
  components: {
    FormGroup,
    PhoenixForm: defineAsyncComponent(() => import('@maison/components/form/PhoenixForm.vue')),
    TextField,
    PhoneField,
    SelectField,
    TextareaField,
    CheckboxField,
    PictureLazy,
  },

  mixins: [formBasicMixin, formAddressFieldsMixin, formContactMixin, formWithImage],

  watch: {
    'fields.preferredContactMode.value'() {
      this.setEmailIsRequired();
      this.setPhoneIsRequired();
    },
  },

  created() {
    this.setFields({
      preferredContactMode: {
        id: 'preferredContactMode',
        name: 'preferredContactMode',
        label: this.$t('Form.contactPreference'),
        value: 'phone',
        options: [
          {
            label: this.$t('Store.boutiqueAppointment.contactMeByCall'),
            value: 'phone',
          },
          {
            label: this.$t('Store.boutiqueAppointment.contactMeByPhoneAndEmail'),
            value: 'Email and Phone',
          },
          {
            label: this.$t('Store.boutiqueAppointment.contactMeByEmail'),
            value: 'email',
          },
        ],
        required: true,
        type: 'select',
      },
      reference: {
        id: 'reference',
        name: 'reference',
        label: this.$t('Form.reference'),
        value: '',
        required: false,
        type: 'text',
      },
    });
  },

  methods: {
    afterSetAddressFieldsHook() {
      this.setEmailIsRequired();
      this.setPhoneIsRequired();
    },

    setEmailIsRequired() {
      if (this.fields.email && window.Phoenix.currentCountry === 'CN') {
        this.fields.email.required = this.fields.preferredContactMode.value !== 'phone';
      }
    },
  },
};
</script>

<template>
  <div :class="wrapperClasses">
    <phoenix-form
      v-model="fields"
      :prefill-enabled="true"
      :redirect-url="redirectUrl"
      :terms-link="termsLink"
      :privacy-link="privacyLink"
      :on-success-callback="addTrackingInformation"
      :captcha-enabled="enableCaptcha"
      :action="action"
      form-type="contact"
      korean-form-type="contact"
      :actions-placement="hasImage ? 'start' : 'default'"
    >
      <template #default="{ shouldDisplayNewsletter }">
        <h1 v-if="title" class="h2 mb-base">{{ title }}</h1>
        <p v-if="subtitle" class="mb-lg">{{ subtitle }}</p>

        <div :class="gridClasses">
          <div :class="columnClasses">
            <form-group :attrs="fields.country">
              <select-field v-model="fields.country.value" />
            </form-group>
            <form-group v-if="fieldIsPresent('title')" :attrs="fields.title">
              <select-field v-model="fields.title.value" />
            </form-group>
            <form-group v-if="fieldIsPresent('gender')" :attrs="fields.gender">
              <select-field v-model="fields.gender.value" />
            </form-group>
            <form-group v-if="fieldIsPresent('firstName2') && isArabicLanguage" :attrs="fields.firstName2">
              <text-field v-model="fields.firstName2.value" />
            </form-group>
            <form-group v-if="fieldIsPresent('lastName2') && isArabicLanguage" :attrs="fields.lastName2">
              <text-field v-model="fields.lastName2.value" />
            </form-group>
            <form-group v-if="fieldIsPresent('firstName1')" :attrs="fields.firstName1">
              <text-field v-model="fields.firstName1.value" />
            </form-group>
            <form-group v-if="fieldIsPresent('lastName1')" :attrs="fields.lastName1">
              <text-field v-model="fields.lastName1.value" />
            </form-group>
            <form-group v-if="fieldIsPresent('firstName2') && !isArabicLanguage" :attrs="fields.firstName2">
              <text-field v-model="fields.firstName2.value" />
            </form-group>
            <form-group v-if="fieldIsPresent('lastName2') && !isArabicLanguage" :attrs="fields.lastName2">
              <text-field v-model="fields.lastName2.value" />
            </form-group>
          </div>
          <div :class="columnClasses">
            <form-group v-if="fieldIsPresent('email')" :attrs="fields.email">
              <text-field v-model="fields.email.value" />
            </form-group>
            <form-group v-if="fieldIsPresent('phone')" :attrs="fields.phone">
              <phone-field v-model="fields.phone.value" />
            </form-group>
            <form-group :attrs="fields.preferredContactMode">
              <select-field v-model="fields.preferredContactMode.value" />
            </form-group>
            <form-group :attrs="fields.subject">
              <component :is="getComponent(fields.subject.type)" v-model="fields.subject.value" />
            </form-group>
            <form-group v-if="shouldDisplayField('reference')" :attrs="fields.reference">
              <text-field v-model="fields.reference.value" />
            </form-group>
            <form-group :attrs="fields.message">
              <textarea-field v-model="fields.message.value" />
            </form-group>
            <form-group v-if="shouldDisplayNewsletter" :show-label="false" :attrs="fields.newsletter">
              <checkbox-field v-model="fields.newsletter.value" />
            </form-group>
          </div>
        </div>
      </template>
    </phoenix-form>

    <div v-if="hasImage" class="max-sm:hidden">
      <div class="picture">
        <picture-lazy v-bind="image" />
      </div>
    </div>
  </div>
</template>
